/** (Kevin 2025-07-23)
 * Host for link preview popovers rendered outside the preview iframe.
 * Uses DS Popover + Popover.Anchor (fixed proxy) positioned from the child
 * link rectangle via postMessage (SHOW/HIDE). Closes on child scroll/resize.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";

import Button from "@replo/design-system/components/button/Button";
import Popover from "@replo/design-system/components/popover/Popover";
import { ExternalLink, Globe } from "lucide-react";

type LinkRectangle = {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
};

type PopoverMessage =
  | { type: "SHOW_LINK_POPOVER"; url: string; rect: LinkRectangle }
  | { type: "HIDE_LINK_POPOVER" };

function cleanUrl(url: string) {
  return url.replace(/^https?:\/\//, "").replace(/^www\./, "");
}

function mapChildRectToViewport(
  frame: HTMLIFrameElement,
  childRectangle: LinkRectangle,
): LinkRectangle {
  const iframeRect = frame.getBoundingClientRect();
  let scaleX = 1;
  let scaleY = 1;
  const cw = frame.contentWindow;
  if (cw && iframeRect.width && iframeRect.height) {
    if (cw.innerWidth) {
      scaleX = iframeRect.width / cw.innerWidth;
    }
    if (cw.innerHeight) {
      scaleY = iframeRect.height / cw.innerHeight;
    }
  }
  return {
    top: iframeRect.top + childRectangle.top * scaleY,
    left: iframeRect.left + childRectangle.left * scaleX,
    bottom: iframeRect.top + childRectangle.bottom * scaleY,
    right: iframeRect.left + childRectangle.right * scaleX,
    width: childRectangle.width * scaleX,
    height: childRectangle.height * scaleY,
  };
}

type PopoverData = { url: string; rect: LinkRectangle } | null;

export function LinkPreviewPopoverHost() {
  const [popover, setPopover] = useState<PopoverData>(null);

  const frameRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent<PopoverMessage>) => {
      const data = event.data;
      if (!data) {
        return;
      }
      if (data.type === "HIDE_LINK_POPOVER") {
        setPopover(null);
        return;
      }

      if (data.type !== "SHOW_LINK_POPOVER") {
        return;
      }

      const findFrameForSource = (
        source: Window | null,
      ): HTMLIFrameElement | null => {
        return (
          Array.from(document.querySelectorAll("iframe")).find(
            (f) => f.contentWindow === source,
          ) ?? null
        );
      };

      const frame =
        frameRef.current ?? findFrameForSource(event.source as Window | null);
      if (!frame) {
        return;
      }
      frameRef.current = frame;

      setPopover({
        url: data.url,
        rect: mapChildRectToViewport(frame, data.rect),
      });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const anchorStyle = useMemo<React.CSSProperties>(() => {
    if (!popover) {
      return { display: "none" };
    }
    return {
      left: Math.round(popover.rect.left),
      top: Math.round(popover.rect.bottom),
      width: Math.max(1, Math.round(popover.rect.width)),
      height: 1,
    } as const;
  }, [popover]);

  if (!popover) {
    return null;
  }

  const cleaned = cleanUrl(popover.url);

  return (
    <Popover.Root
      isOpen={Boolean(popover)}
      onOpenChange={(open) => {
        if (!open) {
          setPopover(null);
        }
      }}
    >
      <Popover.Anchor asChild>
        <div className="fixed pointer-events-none" style={anchorStyle} />
      </Popover.Anchor>
      <Popover.Content
        shouldPreventDefaultOnInteractOutside={false}
        side="top"
        align="center"
        avoidCollisions
        collisionPadding={8}
        disableTriggerFocusOnClose
        variant="unstyled"
      >
        <div className="border-0.5 border-border shadow-popover rounded-lg bg-white px-3 py-2 flex items-center gap-3 min-w-[220px] max-w-[min(20rem,calc(100vw-16px))] overflow-hidden">
          <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-muted">
            <Globe size={18} />
          </div>
          <div
            className="flex-1 min-w-0 text-default truncate"
            title={popover.url}
          >
            {cleaned}
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              window.open(popover.url, "_blank", "noopener");
              setPopover(null);
            }}
            layoutClassName="h-8"
            endEnhancer={<ExternalLink size={12} />}
          >
            Open
          </Button>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
